<template>
  <div>
    <v-alert
      v-if="error"
      type="error"
      outlined
      dense
      class="mb-4"
    >
      {{ error }}
    </v-alert>

    <v-card outlined>
      <v-card-title class="subtitle-1">
        <v-icon left>mdi-cog-outline</v-icon>
        App Config
        <v-spacer />
        <v-btn
          outlined
          small
          color="primary"
          :loading="loading"
          @click="loadConfig"
        >
          <v-icon left small>mdi-refresh</v-icon>
          Refresh
        </v-btn>
      </v-card-title>

      <v-card-text>
        <v-form
          id="app-config-form"
          ref="form"
          v-model="valid"
          @submit.prevent="saveConfig"
        >
          <v-row>
            <v-col cols="12" md="3">
              <v-text-field
                v-model.trim="form.currencyCode"
                label="Currency Code"
                outlined
                dense
                :rules="requiredRules"
              />
            </v-col>

            <v-col cols="12" md="3">
              <v-text-field
                v-model.trim="form.currencySymbol"
                label="Currency Symbol"
                outlined
                dense
                :rules="requiredRules"
              />
            </v-col>

            <v-col cols="12" md="3">
              <v-text-field
                v-model.number="form.currencyDecimalPlaces"
                label="Decimal Places"
                type="number"
                min="0"
                max="6"
                outlined
                dense
                :rules="decimalRules"
              />
            </v-col>

            <v-col cols="12" md="4">
              <v-switch
                v-model="form.timeOfUse"
                label="Time Of Use"
                color="primary"
                inset
              />
            </v-col>

            <v-col cols="12" md="4">
              <v-switch
                v-model="form.splitDemandByTouPeriod"
                label="Split Demand By TOU Period"
                color="primary"
                inset
                :disabled="!form.timeOfUse"
              />
            </v-col>

            <v-col cols="12" md="4">
              <v-switch
                v-model="form.useTouUnitCost"
                label="Use TOU Unit Cost"
                color="primary"
                inset
                :disabled="!form.timeOfUse"
              />
            </v-col>
          </v-row>
        </v-form>
      </v-card-text>

      <v-card-actions class="justify-end">
        <v-btn
          type="submit"
          form="app-config-form"
          color="primary"
          :loading="saving"
          :disabled="!valid"
        >
          <v-icon left>mdi-content-save</v-icon>
          Save App Config
        </v-btn>
      </v-card-actions>
    </v-card>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';

const DEFAULT_FORM = {
  _id: null,
  configKey: 'default',
  currencyCode: 'THB',
  currencySymbol: 'THB',
  currencyDecimalPlaces: 2,
  timeOfUse: true,
  splitDemandByTouPeriod: true,
  useTouUnitCost: true,
  isActive: true,
};

export default {
  name: 'AppConfigManagement',

  data() {
    return {
      valid: true,
      loading: false,
      saving: false,
      error: null,
      form: { ...DEFAULT_FORM },
      requiredRules: [
        (value) => !!value || 'Required',
      ],
      decimalRules: [
        (value) => (
          value !== null && value !== undefined && value !== ''
        ) || 'Required',
        (value) => Number(value) >= 0 || 'Must be zero or greater',
        (value) => Number(value) <= 6 || 'Must be 6 or less',
      ],
    };
  },

  async created() {
    await this.loadConfig();
  },

  computed: {
    ...mapGetters('billingApp', ['billingAppConfig']),
  },

  methods: {
    ...mapActions('billingApp', ['loadBillingAppConfig', 'saveBillingAppConfig']),

    async loadConfig() {
      this.loading = true;
      this.error = null;

      try {
        const config = await this.loadBillingAppConfig();
        this.form = {
          ...DEFAULT_FORM,
          ...(config || this.billingAppConfig || {}),
        };
      } catch (error) {
        this.error = error.message || 'Failed to load app config';
      } finally {
        this.loading = false;
      }
    },

    async saveConfig() {
      if (!this.$refs.form.validate()) {
        return;
      }

      this.saving = true;
      this.error = null;

      try {
        const savedConfig = await this.saveBillingAppConfig(this.form);
        this.form = {
          ...DEFAULT_FORM,
          ...(savedConfig || this.form),
        };
        this.$store.dispatch('showSnackbar', {
          message: 'App config saved successfully',
          color: 'success',
        });
      } catch (error) {
        this.error = error.message || 'Failed to save app config';
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>
